import { checkIfAccountIsRegistered } from 'app-engine/services'
import { GreenButton } from 'app-view/components/buttons'
import { useGlobalModal } from 'app-view/components/GlobalModal/useGlobalModal'
import { HintError, Input, InputSection, InputWrapper, Label } from 'app-view/components/InputField'
import { ModalText } from 'app-view/components/Modal'
import Spinner from 'app-view/components/Spinner'
import { LargeBlackButton, LargeGreenButton } from 'pages/AccountView/components/LargeButtons'
import { AccountFormWrapper, Form, H2, Text } from 'pages/AccountView/components/TextElements'
import { useAccountValidation } from 'pages/AccountView/hooks/useAccountValidation'
import { useRegistration } from 'pages/AccountView/hooks/useRegistration'
import {
  accountInputHandler,
  AccountInputHandlerProps,
  checkAccountExt,
  FormData,
} from 'pages/AccountView/utils'
import React, { useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

export const CreateAccount = () => {
  const { t } = useTranslation('account')
  const location = useLocation()
  const navigate = useNavigate()
  const [{ referrer, website }, { setRegistrationData }] = useRegistration()
  const [{ loading, hint }, { validateUserAccount, updateState }] = useAccountValidation()
  const [, globalModalActions] = useGlobalModal()

  useEffect(() => {
    if (website && website === 'bitlauncher.ai') {
      globalModalActions.open({
        content: BitlauncherWelcomeMessage,
        title: t('bitlauncher_welcome_title'),
        iconType: 'TIP',
        showClose: true,
      })
    }
  }, [website])

  const form = useForm<FormData>({
    defaultValues: {
      account: '',
      referrer: referrer || (new URLSearchParams(location.search).get('referrer') ?? ''),
    },
  })

  const onSubmit = useCallback(
    (data: FormData) => {
      setRegistrationData(data)
      navigate('/create-account/webauthn-cred')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRegistrationData], // navigate is not necessary it is a stable function
  )

  const account_input_props: AccountInputHandlerProps = {
    form,
    field: 'account',
    message: t('create_account_user_account_error_pattern'),
  }

  const isAccountInputValid = Object.keys(form.formState.errors).length === 0

  const shouldOmitSubmit =
    loading || !isAccountInputValid || form.formState.isValidating || form.formState.isSubmitting

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <AccountFormWrapper>
        <div>
          <H2>{t('create_account_title')}</H2>
          <Text
            dangerouslySetInnerHTML={{
              __html: t('create_account_format_description'),
            }}
          />
        </div>
        <Controller
          name="account"
          control={form.control}
          rules={{
            required: t('create_account_user_account_error_required') as string,
            pattern: {
              value: /^([a-z]|[1-5]){1,9}$/,
              message: t('create_account_user_account_error_pattern'),
            },
            validate: () =>
              shouldOmitSubmit
                ? (t('create_account_user_account_error_unavailable') as string)
                : true,
          }}
          render={({ field, fieldState }) => (
            <InputWrapper mt={12}>
              <InputSection inputSize="sm">
                <Input
                  {...field}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (!loading) updateState({ loading: true })
                    if (hint) updateState({ hint: '' })

                    accountInputHandler(event, account_input_props, async () => {
                      await validateUserAccount(form, true)
                    })
                  }}
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    if (!isAccountInputValid) return

                    accountInputHandler(event, account_input_props, async () => {
                      await validateUserAccount(form, true)
                    })
                  }}
                  type="text"
                  placeholder={t('create_account_user_account_placeholder')}
                  maxLength={9}
                  fullradius={1}
                  fontWeight="500"
                  error={Boolean(!form.formState.isValidating && fieldState.error)}
                />
                {loading && field.value && isAccountInputValid && <Spinner boxSize={20} />}
              </InputSection>
              {!form.formState.isValidating && (fieldState.error || hint) && (
                <HintError align="left">{fieldState.error?.message || hint}</HintError>
              )}
            </InputWrapper>
          )}
        />
        <Controller
          name="referrer"
          control={form.control}
          rules={{
            required: t('create_account_referrer_error_required') as string,
            pattern: {
              value: /^([a-z]|[1-5]|.){1,12}$/,
              message: t('create_account_user_account_error_pattern'),
            },
            validate: async (referrer) => {
              const accountExists = await checkIfAccountIsRegistered(
                checkAccountExt(referrer || ''),
              )
              return !accountExists
                ? (t('create_account_referrer_error_unexistent') as string)
                : true
            },
          }}
          render={({ field, fieldState }) => (
            <InputWrapper mb={32} mt={64}>
              <Label size="lg" pl={0} mb={32}>
                {t('create_account_referrer_label')}
              </Label>
              <InputSection inputSize="sm">
                <Input
                  {...field}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    accountInputHandler(event, {
                      form,
                      field: 'referrer',
                      message: t('create_account_referrer_account_error_pattern'),
                      referrer: true,
                    })
                  }
                  placeholder={`${t('create_account_referrer_account_placeholder')}`}
                  maxLength={12}
                  fullradius={1}
                  fontWeight="500"
                  error={Boolean(!form.formState.isValidating && fieldState.error)}
                />
              </InputSection>
              {!form.formState.isValidating && fieldState.error && (
                <HintError align="left">{fieldState.error.message}</HintError>
              )}
            </InputWrapper>
          )}
        />
      </AccountFormWrapper>
      <div>
        <LargeGreenButton type="submit" disabled={shouldOmitSubmit}>
          {t('create_account_accounts_submit')}
        </LargeGreenButton>
        <LargeBlackButton type="button" onClick={() => navigate('/')}>
          {t('p2p:not_now')}
        </LargeBlackButton>
      </div>
    </Form>
  )
}

function BitlauncherWelcomeMessage() {
  const [, globalModalActions] = useGlobalModal()
  const { t } = useTranslation('account')

  return (
    <div style={{ width: '100%', padding: 0, marginBottom: 24 }}>
      <svg
        width="326"
        height="72"
        viewBox="0 0 326 72"
        fill="none"
        style={{
          width: '100%',
          height: 'auto',
          marginBottom: 24,
        }}
      >
        <rect width="391.44" height="72" transform="translate(-32.72)" fill="#060932" />
        <path
          d="M88.0756 14.9199L67.28 57.0808L79.7775 49.8226C83.2787 47.7893 85.5689 44.1769 85.9145 40.1429L88.0756 14.9199Z"
          fill="white"
        />
        <path
          d="M90.9241 15.2031L111.72 56.7943L99.2736 49.6636C95.7426 47.6406 93.4266 44.016 93.0745 39.9618L90.9241 15.2031Z"
          fill="white"
        />
        <path
          d="M120.365 25.5125H124.313V34.6426C125.3 33.2991 126.781 32.8604 128.124 32.8604C129.797 32.8604 131.305 33.491 132.374 34.5055C133.553 35.6296 134.293 37.3021 134.293 39.331C134.293 41.2228 133.635 42.9776 132.374 44.2388C131.305 45.3355 129.989 45.9113 128.179 45.9113C126.013 45.9113 124.916 44.8968 124.313 44.0194V45.5H120.365V25.5125ZM127.165 36.2602C126.315 36.2602 125.574 36.507 124.944 37.165C124.478 37.6585 124.066 38.3714 124.066 39.4133C124.066 40.4277 124.505 41.168 124.971 41.6067C125.602 42.2099 126.479 42.5115 127.219 42.5115C127.905 42.5115 128.673 42.2373 129.276 41.6615C129.852 41.1132 130.235 40.318 130.235 39.4133C130.235 38.4262 129.852 37.6311 129.276 37.0828C128.645 36.4796 127.96 36.2602 127.165 36.2602ZM137.023 33.2717H140.971V45.5H137.023V33.2717ZM136.694 28.0623C136.694 26.7737 137.708 25.7592 138.997 25.7592C140.286 25.7592 141.3 26.7737 141.3 28.0623C141.3 29.351 140.286 30.3654 138.997 30.3654C137.708 30.3654 136.694 29.351 136.694 28.0623ZM150.824 36.4521H148.384V45.5H144.436V36.4521H143.065V33.2717H144.436V29.3235H148.384V33.2717H150.824V36.4521ZM152.629 25.5125H156.577V45.5H152.629V25.5125ZM169.126 34.6426V33.2717H173.074V45.5H169.126V44.0194C168.056 45.6645 166.521 45.9113 165.506 45.9113C163.752 45.9113 162.271 45.4726 160.928 44.0469C159.639 42.676 159.145 41.0857 159.145 39.4681C159.145 37.4118 159.913 35.657 161.037 34.5329C162.107 33.4636 163.587 32.8604 165.287 32.8604C166.329 32.8604 168.001 33.1072 169.126 34.6426ZM166.274 36.2602C165.424 36.2602 164.629 36.6167 164.108 37.1376C163.697 37.5489 163.203 38.2617 163.203 39.3584C163.203 40.4551 163.669 41.168 164.026 41.5518C164.574 42.1276 165.424 42.5115 166.356 42.5115C167.151 42.5115 167.892 42.1825 168.413 41.6615C168.934 41.168 169.372 40.4003 169.372 39.3584C169.372 38.4811 169.043 37.6585 168.468 37.1102C167.864 36.5344 166.987 36.2602 166.274 36.2602ZM176.135 33.2717H180.083V40.0165C180.083 40.5648 180.138 41.4148 180.769 41.9905C181.043 42.2373 181.536 42.5663 182.414 42.5663C183.154 42.5663 183.73 42.3195 184.086 41.9905C184.662 41.4422 184.744 40.6196 184.744 40.0165V33.2717H188.692V40.4003C188.692 41.7986 188.555 43.1147 187.321 44.321C185.923 45.6919 183.867 45.9113 182.414 45.9113C180.906 45.9113 178.877 45.6919 177.479 44.321C176.354 43.2243 176.135 41.9905 176.135 40.6471V33.2717ZM191.772 45.5V33.2717H195.72V34.7248C196.926 33.1894 198.352 32.9427 199.504 32.9427C200.573 32.9427 201.971 33.1346 203.013 34.1765C204.165 35.328 204.247 36.7812 204.247 37.7682V45.5H200.299V39.2213C200.299 38.4262 200.271 37.4118 199.641 36.7812C199.339 36.4796 198.846 36.2328 198.133 36.2328C197.31 36.2328 196.789 36.5618 196.488 36.8908C195.83 37.5489 195.72 38.4262 195.72 39.2213V45.5H191.772ZM216.513 33.5459V37.1376C215.553 36.3425 214.566 36.2054 213.936 36.2054C212.702 36.2054 211.962 36.8086 211.688 37.0828C211.167 37.6037 210.783 38.3988 210.783 39.3858C210.783 40.2906 211.112 41.0583 211.605 41.5793C212.208 42.2373 213.113 42.5663 213.991 42.5663C214.676 42.5663 215.608 42.3744 216.513 41.6067V45.171C215.471 45.7468 214.484 45.9113 213.387 45.9113C211.331 45.9113 209.768 45.171 208.644 44.0743C207.712 43.1695 206.725 41.6341 206.725 39.4133C206.725 37.3295 207.602 35.6845 208.754 34.5877C210.125 33.2991 211.742 32.8604 213.415 32.8604C214.484 32.8604 215.471 33.0523 216.513 33.5459ZM219.246 45.5V25.5125H223.194V34.7248C223.687 34.1216 224.208 33.7104 224.674 33.4636C225.497 33.0249 226.182 32.9427 227.06 32.9427C228.019 32.9427 229.445 33.0798 230.514 34.1765C231.638 35.3006 231.721 36.8086 231.721 37.7408V45.5H227.773V39.2213C227.773 38.3714 227.745 37.3844 227.114 36.7812C226.758 36.4247 226.155 36.2328 225.579 36.2328C224.729 36.2328 224.236 36.6167 224.016 36.836C223.221 37.6037 223.194 38.7827 223.194 39.3858V45.5H219.246ZM237.747 37.7134H243.094C243.066 37.6037 242.874 36.836 242.299 36.2876C241.86 35.8764 241.229 35.6022 240.434 35.6022C239.42 35.6022 238.816 36.0135 238.433 36.3973C238.131 36.7263 237.857 37.165 237.747 37.7134ZM246.74 39.66V40.0713H237.61C237.61 40.7293 237.857 41.6067 238.35 42.155C238.954 42.8131 239.776 42.9227 240.379 42.9227C240.955 42.9227 241.558 42.8131 241.97 42.5663C242.024 42.5389 242.49 42.2373 242.819 41.5518L246.548 41.9357C246 43.663 244.903 44.5678 244.19 45.0065C243.011 45.7193 241.695 45.9113 240.324 45.9113C238.487 45.9113 236.87 45.5823 235.444 44.1565C234.375 43.0872 233.607 41.4696 233.607 39.4407C233.607 37.6859 234.238 35.8764 235.472 34.6152C236.952 33.1346 238.789 32.8604 240.16 32.8604C241.531 32.8604 243.505 33.1072 245.013 34.6974C246.466 36.2328 246.74 38.152 246.74 39.66ZM249.204 45.5V33.2717H253.153V35.1909C253.509 34.5055 254.057 33.8749 254.688 33.491C255.346 33.0798 256.031 32.9701 256.799 32.9701C257.293 32.9701 257.649 33.0249 258.088 33.162L257.868 36.8634C257.347 36.5892 256.772 36.4247 256.086 36.4247C255.236 36.4247 254.606 36.7263 254.112 37.2473C253.235 38.1795 253.153 39.4955 253.153 40.5374V45.5H249.204Z"
          fill="white"
        />
      </svg>
      <ModalText style={{ maxWidth: 300 }}>
        <b>{t('bitlauncher_welcome_body')}</b>
      </ModalText>
      <GreenButton onClick={() => globalModalActions.close()}>{t('ok')}</GreenButton>
    </div>
  )
}
